/**
 * @description Classify Wristband ?req= JWT claims for SignIn routing.
 *
 * Platform hosts (app.lynxpay.us) must bounce SO reqs through login-redirect
 * and keep LynxPay reqs on the local form. Unsigned query is not consulted.
 *
 * @dependencies none
 * @notes
 * - Mirrors backend wristbandLoginSignals decode / van_dom-first LynxPay lock.
 * - JWT decode is routing-only. Wristband verifies the token on submit.
 * - van_dom first (skip LynxPay). LynxPay tnt_id does not lock an SO vanity.
 * - REACT_APP_LYNXPAY_VANITY_HOSTS (comma-separated) adds staging vanities
 *   such as lynxpay-staging-lynx.us.wristband.dev. Unset keeps the static list.
 *   There is no lynxpay- prefix heuristic.
 * - Without REACT_APP_WRISTBAND_DEFAULT_TENANT_ID, tnt_id-only cannot be
 *   proven SO; those stay on the form to avoid a login-redirect loop.
 */

const LYNXPAY_VANITY_HOSTS = new Set([
  'lynxpay.auth.lynx.build',
  'app.lynxpay.us',
  'www.lynxpay.us',
  'lynxpay.us'
]);

/**
 * @param {string|null|undefined} hostname
 * @returns {boolean}
 */
function extraLynxPayVanityHosts() {
  const raw = process.env.REACT_APP_LYNXPAY_VANITY_HOSTS || '';
  return raw.split(',').map((item) => item.trim().toLowerCase()).filter(Boolean);
}

export function isLynxPayVanityHost(hostname) {
  if (!hostname || typeof hostname !== 'string') {
    return false;
  }
  const host = hostname.toLowerCase().split(':')[0];
  if (LYNXPAY_VANITY_HOSTS.has(host) || extraLynxPayVanityHosts().includes(host)) {
    return true;
  }
  return host.startsWith('lynxpay.')
    && (host.endsWith('.wristband.dev') || host.endsWith('.auth.lynx.build'));
}

/**
 * @param {string|null|undefined} tenantId
 * @returns {boolean}
 */
export function isLynxPayTenantId(tenantId) {
  const defaultId = process.env.REACT_APP_WRISTBAND_DEFAULT_TENANT_ID;
  return !!(tenantId && defaultId && tenantId === defaultId);
}

/**
 * Unverified decode of Wristband ?req= JWT claims used for routing.
 *
 * @param {string|null|undefined} reqToken
 * @returns {{ tntId: string|null, vanDom: string|null, tenantName: string|null }}
 */
export function decodeReqTokenClaims(reqToken) {
  if (!reqToken || typeof reqToken !== 'string') {
    return { tntId: null, vanDom: null, tenantName: null };
  }
  try {
    const parts = reqToken.split('.');
    if (parts.length !== 3) {
      return { tntId: null, vanDom: null, tenantName: null };
    }
    const json = decodeJwtPayload(parts[1]);
    const payload = JSON.parse(json);
    const vanDom = payload.van_dom || payload.vanity_domain || payload.tenant_vanity_domain || null;
    return {
      tntId: payload.tnt_id || payload.tenant_id || null,
      vanDom: typeof vanDom === 'string' ? vanDom : null,
      tenantName: payload.tenant_name || null
    };
  } catch {
    return { tntId: null, vanDom: null, tenantName: null };
  }
}

/**
 * @param {string} segment
 * @returns {string}
 */
function decodeJwtPayload(segment) {
  if (typeof Buffer !== 'undefined' && typeof Buffer.from === 'function') {
    return Buffer.from(segment, 'base64url').toString();
  }
  const padded = segment.replace(/-/g, '+').replace(/_/g, '/');
  const pad = padded.length % 4 === 0 ? '' : '='.repeat(4 - (padded.length % 4));
  return atob(padded + pad);
}

/**
 * Whether a decoded ?req= JWT is an explicit LynxPay tenant.
 *
 * @param {{ tntId: string|null, vanDom: string|null }} jwt
 * @returns {boolean}
 */
export function isLynxPayReq(jwt) {
  if (jwt?.vanDom && !isLynxPayVanityHost(jwt.vanDom)) {
    return false;
  }
  return isLynxPayVanityHost(jwt?.vanDom) || isLynxPayTenantId(jwt?.tntId);
}

/**
 * Whether platform SignIn should bounce this ?req= through login-redirect.
 *
 * SO: van_dom is a non-LynxPay vanity, or tnt_id is present and not LynxPay.
 * LynxPay / unclassifiable: stay on the form (no login-redirect loop).
 *
 * @param {string|null|undefined} reqToken
 * @returns {boolean}
 */
export function shouldBouncePlatformReqToSo(reqToken) {
  const jwt = decodeReqTokenClaims(reqToken);
  if (jwt.vanDom && !isLynxPayVanityHost(jwt.vanDom)) {
    return true;
  }
  if (isLynxPayReq(jwt)) {
    return false;
  }
  if (jwt.tntId && !isLynxPayTenantId(jwt.tntId) && !jwt.vanDom) {
    return !!process.env.REACT_APP_WRISTBAND_DEFAULT_TENANT_ID;
  }
  return false;
}
